/*
 * FROSTBEULE · Marquee (owner: HOMEA) – <fb-marquee> in sections/frost-marquee.liquid
 * Every .fb-marquee__row gets one WAAPI loop (linear, translate3d 0 → ∓50 %). The track is filled with
 * copies of the group until half of it covers the viewport, so the loop is seamless at any width; the
 * duration scales with the number of copies (constant speed per group).
 * - Hover (fine pointer) eases the playback rate down to 0.3 and back (no jump, updatePlaybackRate).
 * - Pause button eases to a stop, then pauses (WCAG 2.2.2); play eases back up.
 * - Offscreen (IntersectionObserver) and theme-editor block selection pause immediately.
 * - Motion 'soft' = half speed, 'none' = no loop (static wrapped list via CSS).
 * Custom element: survives shopify:section:load/unload.
 */
(function () {
  'use strict';
  if (!window.customElements || window.customElements.get('fb-marquee')) return;

  var root = document.documentElement;

  function motion() {
    if (window.FROSTBEULE && typeof window.FROSTBEULE.motion === 'function') return window.FROSTBEULE.motion();
    return root.getAttribute('data-motion') || 'full';
  }

  var finePointer = null;
  try {
    finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  } catch (e) {
    finePointer = null;
  }

  var HOVER_RATE = 0.3;

  class FbMarquee extends HTMLElement {
    connectedCallback() {
      this.rows = Array.prototype.map.call(this.querySelectorAll('.fb-marquee__row'), function (row) {
        return {
          row: row,
          track: row.querySelector('.fb-marquee__track'),
          dir: row.getAttribute('data-dir') === 'right' ? 1 : -1,
          anim: null,
          width: 0
        };
      });
      this.toggle = this.querySelector('.fb-marquee__toggle');
      this.speed = parseFloat(this.getAttribute('data-speed')) || 40;
      this.labelPause = this.getAttribute('data-label-pause') || 'Pause';
      this.labelPlay = this.getAttribute('data-label-play') || 'Play';

      this.rate = 1;
      this.hover = false;
      this.userPaused = false;
      this.offscreen = false;
      this.editing = false;
      this.tweenRaf = 0;
      this.buildRaf = 0;
      this.lastWidth = 0;

      this.onToggle = this.onToggle.bind(this);
      this.onEnter = this.onEnter.bind(this);
      this.onLeave = this.onLeave.bind(this);
      this.onMotion = this.onMotion.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onBlockDeselect = this.onBlockDeselect.bind(this);
      this.scheduleBuild = this.scheduleBuild.bind(this);

      if (this.toggle) this.toggle.addEventListener('click', this.onToggle);
      this.addEventListener('pointerenter', this.onEnter);
      this.addEventListener('pointerleave', this.onLeave);
      document.addEventListener('frost:motion-change', this.onMotion);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

      if ('ResizeObserver' in window) {
        var self = this;
        this.ro = new ResizeObserver(function () {
          self.scheduleBuild();
        });
        this.ro.observe(this);
        this.rows.forEach(function (r) {
          var first = r.track && r.track.querySelector('.fb-marquee__group');
          if (first) self.ro.observe(first);
        });
      } else {
        window.addEventListener('resize', this.scheduleBuild);
      }

      if ('IntersectionObserver' in window) {
        var me = this;
        this.io = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              me.offscreen = !entry.isIntersecting;
              me.applyState(true);
            });
          },
          { rootMargin: '120px 0px' }
        );
        this.io.observe(this);
      }

      this.build();
    }

    disconnectedCallback() {
      if (this.toggle) this.toggle.removeEventListener('click', this.onToggle);
      this.removeEventListener('pointerenter', this.onEnter);
      this.removeEventListener('pointerleave', this.onLeave);
      document.removeEventListener('frost:motion-change', this.onMotion);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      window.removeEventListener('resize', this.scheduleBuild);
      if (this.ro) this.ro.disconnect();
      if (this.io) this.io.disconnect();
      if (this.tweenRaf) window.cancelAnimationFrame(this.tweenRaf);
      if (this.buildRaf) window.cancelAnimationFrame(this.buildRaf);
      this.cancelAnims();
    }

    scheduleBuild() {
      if (this.buildRaf) return;
      var self = this;
      this.buildRaf = window.requestAnimationFrame(function () {
        self.buildRaf = 0;
        self.build();
      });
    }

    cancelAnims() {
      this.rows.forEach(function (r) {
        if (r.anim) r.anim.cancel();
        r.anim = null;
      });
    }

    build() {
      var m = motion();
      var canAnimate = typeof Element.prototype.animate === 'function';
      if (m === 'none' || !canAnimate) {
        this.cancelAnims();
        this.removeAttribute('data-running');
        if (this.toggle) this.toggle.hidden = true;
        return;
      }

      var viewW = this.getBoundingClientRect().width;
      if (!viewW) return;
      var factor = m === 'soft' ? 2 : 1;
      var self = this;
      var built = 0;

      this.rows.forEach(function (r, index) {
        if (!r.track) return;
        var groups = r.track.querySelectorAll('.fb-marquee__group');
        if (groups.length < 2) return;
        var gw = groups[0].getBoundingClientRect().width;
        if (!gw) return;

        var k = Math.max(1, Math.ceil(viewW / gw));
        var want = k * 2;
        var count = groups.length;
        while (count < want) {
          r.track.appendChild(groups[1].cloneNode(true));
          count += 1;
        }
        while (count > want && count > 2) {
          r.track.removeChild(r.track.lastElementChild);
          count -= 1;
        }

        var duration = self.speed * 1000 * k * factor * (index === 1 ? 1.18 : 1);
        var progress = 0;
        if (r.anim && r.anim.effect) {
          var timing = r.anim.effect.getComputedTiming();
          progress = timing && typeof timing.progress === 'number' ? timing.progress : 0;
          if (r.width === gw && Math.abs(timing.duration - duration) < 1) {
            built += 1;
            return; // nothing changed for this row
          }
          r.anim.cancel();
        }

        var from = 'translate3d(0, 0, 0)';
        var to = 'translate3d(-50%, 0, 0)';
        var frames = r.dir < 0 ? [{ transform: from }, { transform: to }] : [{ transform: to }, { transform: from }];
        r.anim = r.track.animate(frames, { duration: duration, iterations: Infinity, easing: 'linear' });
        r.anim.currentTime = progress * duration;
        r.anim.playbackRate = self.rate;
        r.width = gw;
        built += 1;
      });

      if (!built) return;
      this.setAttribute('data-running', '');
      if (this.toggle) this.toggle.hidden = false;
      this.applyState(true);
    }

    paused() {
      return this.userPaused || this.offscreen || this.editing;
    }

    eachAnim(fn) {
      this.rows.forEach(function (r) {
        if (r.anim) fn(r.anim);
      });
    }

    setRate(rate) {
      this.eachAnim(function (a) {
        if (typeof a.updatePlaybackRate === 'function') a.updatePlaybackRate(rate);
        else a.playbackRate = rate;
      });
    }

    tweenTo(target, done) {
      this.tweenTarget = target;
      this.tweenDone = done || null;
      if (this.tweenRaf) return;
      var self = this;
      var last = performance.now();
      function step(now) {
        var dt = Math.min(0.05, (now - last) / 1000);
        last = now;
        // exponential approach (time constant 160 ms)
        self.rate += (self.tweenTarget - self.rate) * (1 - Math.exp(-dt / 0.16));
        if (Math.abs(self.tweenTarget - self.rate) < 0.01) self.rate = self.tweenTarget;
        self.setRate(self.rate);
        if (self.rate === self.tweenTarget) {
          self.tweenRaf = 0;
          var cb = self.tweenDone;
          self.tweenDone = null;
          if (cb) cb();
          return;
        }
        self.tweenRaf = window.requestAnimationFrame(step);
      }
      this.tweenRaf = window.requestAnimationFrame(step);
    }

    applyState(immediate) {
      var self = this;
      this.classList.toggle('is-paused', this.userPaused);
      if (this.toggle) this.toggle.setAttribute('aria-label', this.userPaused ? this.labelPlay : this.labelPause);

      if (this.offscreen || this.editing) {
        if (this.tweenRaf) window.cancelAnimationFrame(this.tweenRaf);
        this.tweenRaf = 0;
        this.eachAnim(function (a) {
          a.pause();
        });
        return;
      }

      if (this.userPaused) {
        if (immediate) {
          this.rate = 0;
          this.eachAnim(function (a) {
            a.pause();
          });
          return;
        }
        this.tweenTo(0, function () {
          if (self.userPaused) {
            self.eachAnim(function (a) {
              a.pause();
            });
          }
        });
        return;
      }

      this.eachAnim(function (a) {
        if (a.playState !== 'running') a.play();
      });
      var target = this.hover ? HOVER_RATE : 1;
      if (immediate && this.rate === target) {
        this.setRate(target);
        return;
      }
      this.tweenTo(target);
    }

    onToggle() {
      this.userPaused = !this.userPaused;
      this.applyState(false);
    }

    onEnter(event) {
      if (event.pointerType && event.pointerType !== 'mouse') return;
      if (finePointer && !finePointer.matches) return;
      this.hover = true;
      if (!this.paused()) this.tweenTo(HOVER_RATE);
    }

    onLeave() {
      if (!this.hover) return;
      this.hover = false;
      if (!this.paused()) this.tweenTo(1);
    }

    onMotion() {
      this.cancelAnims();
      this.rows.forEach(function (r) {
        r.width = 0;
      });
      this.build();
    }

    onBlockSelect(event) {
      if (!this.contains(event.target)) return;
      this.editing = true;
      this.applyState(true);
    }

    onBlockDeselect(event) {
      if (!this.contains(event.target)) return;
      this.editing = false;
      this.applyState(true);
    }
  }

  window.customElements.define('fb-marquee', FbMarquee);
})();
